import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import {
  Zap,
  List,
  Plug,
  History,
  Activity,
  AlertCircle,
  PlayCircle,
  XCircle,
  Loader,
  RefreshCw,
  Clock,
  GitBranch,
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

// Import components
import JobList from './components/JobList';
import EndpointList from './components/EndpointList';
import HistoryViewer from './components/HistoryViewer';
import WorkflowList from './components/WorkflowList';

const Automation = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();

  const [activeTab, setActiveTab] = useState('jobs');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [delayedJobs, setDelayedJobs] = useState([]);
  const [delayedLoading, setDelayedLoading] = useState(false);
  const [jobAction, setJobAction] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Check if user is admin
  const isAdmin = user?.roles?.some(role => role.RoleName === 'Admin' || role === 'Admin');

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadStats();
      checkHealth();
      loadDelayedJobs();
    }
  }, [isAdmin]);

  const loadStats = async () => {
    try {
      const tenantCode = getProtectedTenantCode();
      const response = await axios.get('/api/automation/stats', {
        headers: {
          'X-Tenant-Code': tenantCode,
        },
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load automation stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkHealth = async () => {
    try {
      const tenantCode = getProtectedTenantCode();
      const response = await axios.get('/api/automation/health', {
        headers: {
          'X-Tenant-Code': tenantCode,
        },
      });
      setHealth(response.data);
    } catch (error) {
      console.error('Failed to check automation health:', error);
      setHealth({ status: 'unhealthy' });
    }
  };

  const loadDelayedJobs = async () => {
    try {
      setDelayedLoading(true);
      const tenantCode = getProtectedTenantCode();
      const response = await axios.get('/api/automation/queue/delayed', {
        headers: { 'X-Tenant-Code': tenantCode },
        params: { limit: 50 },
      });
      setDelayedJobs(response.data);
    } catch (error) {
      console.error('Failed to load delayed jobs:', error);
      toast.error('Failed to load delayed jobs');
    } finally {
      setDelayedLoading(false);
    }
  };

  const promoteDelayedJob = async (job) => {
    try {
      setJobAction(`${job.bullJobId}-promote`);
      const tenantCode = getProtectedTenantCode();
      await axios.post(
        `/api/automation/queue/delayed/${job.bullJobId}/promote`,
        {},
        { headers: { 'X-Tenant-Code': tenantCode } }
      );
      toast.success('Job promoted successfully');
      loadDelayedJobs();
      checkHealth();
    } catch (error) {
      console.error('Failed to promote job:', error);
      toast.error(error.response?.data?.error || 'Failed to promote job');
    } finally {
      setJobAction(null);
    }
  };

  const cancelDelayedJob = async (job) => {
    try {
      setJobAction(`${job.bullJobId}-cancel`);
      const tenantCode = getProtectedTenantCode();
      await axios.delete(`/api/automation/queue/delayed/${job.bullJobId}`, {
        headers: { 'X-Tenant-Code': tenantCode },
      });
      toast.success('Job removed from queue');
      // Refresh the list to remove the job from UI
      await loadDelayedJobs();
      checkHealth();
    } catch (error) {
      console.error('Failed to remove job:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to remove job';
      
      // If job not found, it may have already been processed - refresh the list
      if (errorMessage.includes('not found') || errorMessage.includes('already been processed')) {
        toast.success('Job is no longer in the queue (may have already been processed)');
        await loadDelayedJobs();
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setJobAction(null);
    }
  };

  // Access control
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 dark:text-red-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-gray-100">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            You need Administrator privileges to access the Automation system.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'jobs', name: 'Jobs', icon: Zap, count: stats?.totalJobs },
    { id: 'endpoints', name: 'Endpoints', icon: Plug, count: stats?.totalEndpoints },
    { id: 'workflows', name: 'Workflows', icon: GitBranch, count: stats?.totalWorkflows },
    { id: 'history', name: 'History', icon: History },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden transition-colors duration-300">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-12 h-12 bg-white bg-opacity-20 rounded-lg">
                <Zap className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Automation</h1>
                <p className="text-blue-100 mt-1">
                  Schedule and manage automated tasks
                </p>
              </div>
            </div>

            {/* Current Time Clock */}
            <div className="flex items-center space-x-4">
              {/* Server Time Display */}
              <div className="bg-white bg-opacity-20 backdrop-blur-sm px-4 py-2 rounded-lg border border-white border-opacity-30">
                <div className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-white" />
                  <div className="text-left">
                    <div className="text-xs text-white text-opacity-80 font-medium">Server Time (UTC+7)</div>
                    <div className="text-lg font-bold text-white tabular-nums">
                      {currentTime.toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false
                      })}
                    </div>
                    <div className="text-xs text-white text-opacity-70">
                      {currentTime.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Health Status */}
              {health && (
                <div className="flex items-center space-x-2 bg-white bg-opacity-20 px-4 py-2 rounded-lg">
                  <Activity
                    className={`h-5 w-5 ${health.status === 'healthy' ? 'text-green-300' : 'text-red-300'
                      }`}
                  />
                  <span className="text-sm font-medium">
                    {health.status === 'healthy' ? 'System Healthy' : 'System Issue'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          {stats && !loading && (
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-30 transition-colors duration-300">
                <div className="text-sm font-medium text-white text-opacity-90">Total Jobs</div>
                <div className="mt-1 text-2xl font-semibold text-white">
                  {stats.totalJobs}
                </div>
                <div className="mt-1 text-xs text-white text-opacity-80">
                  {stats.enabledJobs} enabled
                </div>
              </div>

              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-30 transition-colors duration-300">
                <div className="text-sm font-medium text-white text-opacity-90">Scheduled Jobs</div>
                <div className="mt-1 text-2xl font-semibold text-white">
                  {stats.scheduledJobs}
                </div>
                <div className="mt-1 text-xs text-white text-opacity-80">
                  Active schedules
                </div>
              </div>

              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-30 transition-colors duration-300">
                <div className="text-sm font-medium text-white text-opacity-90">Endpoints</div>
                <div className="mt-1 text-2xl font-semibold text-white">
                  {stats.totalEndpoints}
                </div>
                <div className="mt-1 text-xs text-white text-opacity-80">
                  {stats.systemEndpoints} system
                </div>
              </div>

              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-30 transition-colors duration-300">
                <div className="text-sm font-medium text-white text-opacity-90">Recent Failures</div>
                <div className="mt-1 text-2xl font-semibold text-white">
                  {stats.recentFailures}
                </div>
                <div className="mt-1 text-xs text-white text-opacity-80">
                  Last 24 hours
                </div>
              </div>
            </div>
          )}

          {/* Automation Runtime Status */}
          {health && (
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-30 transition-colors duration-300">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-white text-opacity-90">Processor Status</div>
                    <div className="mt-1 text-2xl font-semibold text-white">
                      {health.processorRunning ? 'Running' : 'Stopped'}
                    </div>
                  </div>
                  <Activity className={`h-8 w-8 ${health.processorRunning ? 'text-green-200' : 'text-red-200'}`} />
                </div>
                <div className="mt-3 text-xs text-white text-opacity-80 space-y-1">
                  <div>
                    Global Processor:{' '}
                    <span className="font-semibold">
                      {health.processorDetails?.isProcessing ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    Tenant Processor:{' '}
                    <span className="font-semibold">
                      {health.processorDetails?.tenantProcessorActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    Active Tenants:{' '}
                    <span className="font-semibold">
                      {health.processorDetails?.activeProcessors?.length || 0}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-30 transition-colors duration-300">
                <div className="text-sm font-medium text-white text-opacity-90">Queue (Current Tenant)</div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-white text-opacity-90">
                  <div>
                    <div className="text-xs text-white text-opacity-80">Waiting</div>
                    <div className="text-xl font-semibold">{health.queueStats?.waiting ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-xs text-white text-opacity-80">Active</div>
                    <div className="text-xl font-semibold">{health.queueStats?.active ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-xs text-white text-opacity-80">Delayed</div>
                    <div className="text-xl font-semibold">{health.queueStats?.delayed ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-xs text-white text-opacity-80">Failed</div>
                    <div className="text-xl font-semibold">{health.queueStats?.failed ?? 0}</div>
                  </div>
                </div>
                <div className="mt-3 text-xs text-white text-opacity-80">
                  Global Waiting:{' '}
                  <span className="font-semibold">
                    {health.globalQueueStats?.waiting ?? 0}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Delayed Jobs Panel */}
          {health && (
            <div className="mt-6 bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-30 transition-colors duration-300">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-sm font-medium text-white text-opacity-90">Delayed Jobs</div>
                  <div className="text-xs text-white text-opacity-80">
                    {health.queueStats?.delayed ?? 0} delayed jobs in tenant queue
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={loadDelayedJobs}
                    disabled={delayedLoading}
                    className="inline-flex items-center px-3 py-2 border border-white border-opacity-30 rounded-md text-xs font-medium text-white bg-white bg-opacity-10 hover:bg-opacity-20 disabled:opacity-50"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </button>
                </div>
              </div>

              {delayedLoading ? (
                <div className="flex items-center justify-center py-6 text-white text-opacity-80">
                  <Loader className="h-5 w-5 animate-spin mr-2" />
                  Loading delayed jobs...
                </div>
              ) : delayedJobs.length === 0 ? (
                <div className="text-sm text-white text-opacity-80 py-4">
                  No delayed jobs at the moment.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-white divide-opacity-20 text-white text-opacity-90 text-sm">
                    <thead className="text-xs uppercase text-white text-opacity-70">
                      <tr>
                        <th className="px-3 py-2 text-left">Job</th>
                        <th className="px-3 py-2 text-left">Scheduled</th>
                        <th className="px-3 py-2 text-left">Endpoint</th>
                        <th className="px-3 py-2 text-left">Delay</th>
                        <th className="px-3 py-2 text-left">Bull ID</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white divide-opacity-10">
                      {delayedJobs.map(job => (
                        <tr key={job.bullJobId}>
                          <td className="px-3 py-2">
                            <div className="font-medium">{job.jobName || 'Unnamed Job'}</div>
                            <div className="text-xs text-white text-opacity-70">JobID: {job.jobId || '-'}</div>
                          </td>
                          <td className="px-3 py-2">
                            <div>{job.scheduledAt ? new Date(job.scheduledAt).toLocaleString() : '-'}</div>
                            <div className="text-xs text-white text-opacity-70">
                              Created: {job.timestamp ? new Date(job.timestamp).toLocaleString() : '-'}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-xs break-all">{job.endpointURL || '-'}</div>
                          </td>
                          <td className="px-3 py-2">
                            {job.delay ? `${(job.delay / 1000).toFixed(1)}s` : 'Immediate'}
                          </td>
                          <td className="px-3 py-2 text-xs">{job.bullJobId}</td>
                          <td className="px-3 py-2 text-right space-x-2">
                            <button
                              onClick={() => promoteDelayedJob(job)}
                              disabled={jobAction === `${job.bullJobId}-promote`}
                              className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md bg-green-500 bg-opacity-90 text-white hover:bg-green-400 disabled:opacity-50"
                            >
                              <PlayCircle className="h-4 w-4 mr-1" />
                              Run now
                            </button>
                            <button
                              onClick={() => cancelDelayedJob(job)}
                              disabled={jobAction === `${job.bullJobId}-cancel`}
                              className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md bg-red-500 bg-opacity-90 text-white hover:bg-red-400 disabled:opacity-50"
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Cancel
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200
                    ${activeTab === tab.id
                      ? 'border-teal-500 dark:border-teal-400 text-teal-600 dark:text-teal-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  <Icon
                    className={`
                      -ml-0.5 mr-2 h-5 w-5 transition-colors duration-200
                      ${activeTab === tab.id
                        ? 'text-teal-500 dark:text-teal-400'
                        : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'
                      }
                    `}
                  />
                  {tab.name}
                  {tab.count !== undefined && (
                    <span
                      className={`
                        ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium transition-colors duration-200
                        ${activeTab === tab.id
                          ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-300'
                        }
                      `}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'jobs' && <JobList onStatsChange={loadStats} />}
        {activeTab === 'endpoints' && <EndpointList />}
        {activeTab === 'workflows' && <WorkflowList onStatsChange={loadStats} />}
        {activeTab === 'history' && <HistoryViewer />}
      </div>
    </div>
  );
};

export default Automation;

